import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Bell, Shield, User, ChevronDown, LayoutDashboard, LogOut, X, Sparkles, Gift, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BUSINESS_NAME, SUITE_NAME } from '@/branding';

/**
 * DashboardNavbar - Top navigation bar for user dashboard
 * Includes logo, notifications, admin button, and user menu
 */
const DashboardNavbar = ({
  user,
  notifications,
  unreadCount,
  showNotifications,
  setShowNotifications,
  userMenuOpen,
  setUserMenuOpen,
  userMenuRef,
  hasMyInsulAccess,
  onMarkRead,
  onMarkAllRead,
  onLogout,
}) => {
  const navigate = useNavigate();

  return (
    <nav className="w-full bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-40 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-[#8FD14F] flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
              <span className="text-lg font-bold text-white uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>IN</span>
            </div>
            <span className="text-xl font-bold tracking-tight hidden sm:block" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {BUSINESS_NAME}
            </span>
          </Link>
          
          {/* Nav Actions */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Home button */}
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full"
              onClick={() => navigate('/')}
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline ml-2">Home</span>
            </Button>

            {/* Notifications dropdown */}
            <div className="relative">
              <button
                data-testid="notifications-button"
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 rounded-full hover:bg-muted/50 transition-colors"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              
              {showNotifications && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-card rounded-xl border border-border shadow-xl z-50">
                  <div className="p-4 border-b border-border flex justify-between items-center">
                    <h3 className="font-semibold">Notifications</h3>
                    {notifications.some(n => !n.is_read) && (
                      <button
                        onClick={onMarkAllRead}
                        className="text-xs text-primary hover:underline"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="p-4 text-sm text-muted-foreground text-center">No notifications</p>
                    ) : (
                      notifications.map(notif => (
                        <div
                          key={notif.id}
                          className={`p-3 border-b border-border/50 last:border-0 ${!notif.is_read ? 'bg-primary/5' : ''}`}
                        >
                          <div className="flex justify-between items-start gap-2">
                            <p className="text-sm">{notif.message}</p>
                            {!notif.is_read && (
                              <button 
                                onClick={() => onMarkRead(notif.id)}
                                className="text-muted-foreground hover:text-foreground"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(notif.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Admin button */}
            {user.is_admin && (
              <Button
                data-testid="admin-dashboard-button"
                variant="outline"
                size="sm"
                className="rounded-full px-2 sm:px-4 py-2 font-semibold border-2 border-secondary text-secondary hover:bg-secondary/10"
                onClick={() => navigate('/admin')}
              >
                <Shield className="w-4 h-4" />
                <span className="hidden sm:inline ml-2">Admin</span>
              </Button>
            )}
            
            {/* User Dropdown Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                data-testid="user-menu-button"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 bg-card rounded-full border border-border/50 hover:bg-muted/50 transition-colors"
              >
                <User className="w-4 h-4 text-primary" />
                <span className="hidden sm:inline text-sm font-medium max-w-[150px] truncate">{user.email}</span>
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full mt-2 w-48 bg-card rounded-xl border border-border shadow-xl z-50 overflow-hidden"
                  >
                    <div className="p-1">
                      <button
                        data-testid="user-dashboard-menu-item"
                        onClick={() => {
                          setUserMenuOpen(false);
                          navigate('/dashboard');
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left text-sm font-medium hover:bg-muted transition-colors"
                      >
                        <LayoutDashboard className="w-4 h-4 text-primary" />
                        User Dashboard
                      </button>
                      <button
                        data-testid="user-profile-menu-item"
                        onClick={() => {
                          setUserMenuOpen(false);
                          navigate('/profile');
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left text-sm font-medium hover:bg-muted transition-colors"
                      >
                        <User className="w-4 h-4 text-primary" />
                        User Profile
                      </button>
                      {hasMyInsulAccess && (
                        <button
                          data-testid="myinsul-menu-item"
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate('/myinsul');
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left text-sm font-medium hover:bg-muted transition-colors text-emerald-600"
                        >
                          <Sparkles className="w-4 h-4 text-emerald-500" />
                          {SUITE_NAME}
                        </button>
                      )}
                      <button
                        data-testid="insul-rewards-menu-item"
                        onClick={() => {
                          setUserMenuOpen(false);
                          navigate('/rewards');
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left text-sm font-medium hover:bg-muted transition-colors text-amber-600"
                      >
                        <Gift className="w-4 h-4 text-amber-500" />
                        InsulRewards
                      </button>
                      <div className="h-px bg-border my-1" />
                      <button
                        data-testid="logout-menu-item"
                        onClick={() => {
                          setUserMenuOpen(false);
                          onLogout();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default DashboardNavbar;
